import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ThemeProvider } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import DainTheme from '../DainTheme';
import { useGetMediaSourcesSummary } from '../api/MonitoringApi';
import RoutePaths from '../routes/RoutePaths';
import {
    MONITORING_CONTENT_CONTAINER_SX,
    MONITORING_HEADER_CONTAINER_SX,
} from './monitoringLayout';
import SourceSummaryTable from './SourceSummaryTable';

export default function MediaDetail() {
    const intl = useIntl();
    const navigate = useNavigate();
    const { mediaId } = useParams();
    const mediaQuery = useGetMediaSourcesSummary({ mediaId });

    const media = mediaQuery?.data?.result ?? null;
    const sources = media?.sources ?? [];

    return (
        <ThemeProvider theme={DainTheme.light}>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        flex: '0 0 auto',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1,
                    }}
                >
                    <Container maxWidth={false} disableGutters sx={MONITORING_HEADER_CONTAINER_SX}>
                        <Stack alignItems="center" spacing={0.5}>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: 'primary.main', fontFamily: 'monospace' }}
                            >
                                {media?.name ?? mediaId}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {intl.formatMessage({ id: 'monitoring.detail.subtitle' })}
                            </Typography>
                        </Stack>
                    </Container>
                </Box>

                <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Container
                        maxWidth={false}
                        disableGutters
                        sx={MONITORING_CONTENT_CONTAINER_SX}
                    >
                        <Button
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(RoutePaths.monitoring.root)}
                            sx={{ mb: 1, flexShrink: 0, alignSelf: 'flex-start' }}
                        >
                            {intl.formatMessage({ id: 'monitoring.detail.back' })}
                        </Button>

                        {mediaQuery.isLoading && (
                            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.loading' })}
                            </Typography>
                        )}
                        {mediaQuery.isError && (
                            <Typography variant="body2" color="error" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.detail.error' })}
                            </Typography>
                        )}
                        {!mediaQuery.isLoading &&
                            !mediaQuery.isError &&
                            sources.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                    {intl.formatMessage({ id: 'monitoring.detail.empty' })}
                                </Typography>
                            )}
                        {sources.length > 0 && (
                            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                <SourceSummaryTable sources={sources} mediaId={mediaId} />
                            </Box>
                        )}
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
